import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  IconButton,
  CircularProgress,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { FaEye, FaEyeSlash, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaFacebook } from 'react-icons/fa';
import "react-toastify/dist/ReactToastify.css";
import { ShimmerSimpleGallery } from "react-shimmer-effects";
import OrganizationsSignin from "../../../assets/organizationsSignin.webp";
import Headrepage from "../../mainpage/Headerpages/OrganizationsHeaderpage";
import Footer from "../../mainpage/FooterOrganizations";
import { OrganizationsLogin } from "../../../helper/base_url";
import { isTokenExpired } from "../../../utils/tokenUtils";
import useNetworkStatus from "../../../helper/NetworkStatus";

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [data, setData] = useState({ username: "", password: "", role: "company" });
  const isMobile = useMediaQuery("(max-inline-size:600px)");
  const isTablet = useMediaQuery("(min-inline-size:601px) and (max-inline-size:960px)");
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();

  // Check token validity
  useEffect(() => {
    const token = localStorage.getItem("company_token");
    if (token) {
      if (isTokenExpired(token)) {
        localStorage.removeItem("company_token");
        toast.warning("Session expired. Please log in again.");
        setCheckingToken(false);
      } else {
        setTimeout(() => navigate("/organizations/dashboard/pending-project"), 1000);
      }
    } else {
      setCheckingToken(false);
    }
  }, [navigate]);

  // Email and Password validation
  const validateEmail = (email) => {
    const emailRegex = /^(?!.*\.\.)[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,}$/;
    return passwordRegex.test(password);
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));

    // Realtime validation messages
    if (name === "username") {
      if (!validateEmail(value)) {
        setErrors((prev) => ({ ...prev, email: "Invalid email format" }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }

    if (name === "password") {
      if (!validatePassword(value)) {
        setErrors((prev) => ({
          ...prev,
          password:
            "Password must be 6+ chars with uppercase, lowercase, number, and special character",
        }));
      } else {
        setErrors((prev) => ({ ...prev, password: "" }));
      }
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    // Prevent API call if invalid
    if (!validateEmail(data.username)) {
      toast.error("Please enter a valid email format.");
      return;
    }
    if (!validatePassword(data.password)) {
      toast.error(
        "Password must be 6+ chars with uppercase, lowercase, number, and special character."
      );
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Processing your login...");

    try {
      const response = await axios.post(`${OrganizationsLogin}`, data, {
        headers: { "Content-Type": "application/json" },
      });
      const { message, token, error } = response.data;

      toast.update(toastId, {
        render: message,
        type: error ? "error" : "success",
        isLoading: false,
        autoClose: 1000,
      });

      if (!error) {
        localStorage.setItem("company_token", token);
        // Add a nice delay for UX before navigating
        setTimeout(() => navigate("/organizations/dashboard/pending-project"), 1000);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Network error, please try again.";
      toast.update(toastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Show shimmer while checking token
  if (checkingToken) {
    return (
      <Box sx={{
        height : "100vh", width : "100%", display: "flex",
        justifyContent: "center", alignItems: "center",
        bgcolor: "#f5f7fa", padding: "20px"
      }}>
        <Box sx={{
          width : { xs: "90%", sm: "70%", md: "50%", lg: "40%" },
          padding: 3, borderRadius: 3, boxShadow: 3, bgcolor: "#ffffff"
        }}>
          <Skeleton variant="text" height={50} width="80%" sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={40} width="100%" sx={{ mb: 3 }} />
          <Skeleton variant="text" width="60%" />
        </Box>
      </Box>
    );
  }

  return (
    <div className="students-flex">
      <Headrepage />
      {isOnline ? (
      <div className="homepagecolores">
      <div style={{ width : "100%", overflowX: "hidden", overflowY: "auto", height : "91vh" }}>
        <ToastContainer position="top-center" />
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ padding: "40px 20px" }}
        >
          <Grid item xs={12} sm={8} md={6} lg={4}>
            <Box>
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                sx={{ color: "#043885", fontWeight: 600 }}
              >
                Log in to your Organization Account
              </Typography>

              <form onSubmit={submitHandler}>
                <TextField
                  label="Email"
                  type="email"
                  name="username"
                  fullWidth
                  margin="normal"
                  value={data.username}
                  onChange={handleOnChange}
                  required
                  disabled={loading}
                  error={!!errors.email}
                  helperText={errors.email}
                  autoComplete="username"
                />
                <Box position="relative">
                  <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  fullWidth
                  margin="normal"
                  value={data.password}
                  onChange={handleOnChange}
                  required
                  disabled={loading}
                  error={!!errors.password}
                  helperText={errors.password}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                        {showPassword ? <FaEye /> : <FaEyeSlash />}
                      </IconButton>
                    ),
                  }}
                />
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    bgcolor: "#043885",
                    "&:hover": { bgcolor: "#052659" },
                    borderRadius: 2,
                    py: 1.2,
                    fontWeight: 600,
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
                </Button>
              </form>

              <Typography sx={{ mt: 2 }}>
                <Link to="/organizations/forgot-password">Forgot Password?</Link>
              </Typography>

              <Typography align="center" sx={{ mt: 2 }}>
                Don’t have an account?{" "}
                <Link to="/organizations/signup" style={{ color: "#043885" }}>
                  Create an account
                </Link>
              </Typography>
            </Box>
          </Grid>

          {!isMobile && !isTablet && (
            <Grid
              item
              xs={12}
              sm={8}
              md={6}
              lg={4}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={OrganizationsSignin}
                alt="Signup Illustration"
                style={{
                  width : "100%",
                  maxWidth : "500px",
                  height : "100%",
                  padding: "20px",
                  borderRadius: "10px",
                }}
              />
            </Grid>
          )}
        </Grid>
        </div>
        <div className="homepage-footers-images">
          <Footer />
        <footer className="homepage-footers">
        <hr className='homepage-footer-hr'/>
        <div className="learners-footers-seokeywords">
        </div>
          <div className="footer-social">
          <div className="homepage-footer-copyright">
            <p>© Huebits[2025].All Rights Reserved.</p>
          </div>
          <div className='footer-social-icons'>
            <Link to="https://www.linkedin.com/company/huebits/mycompany" target="_blank" rel="noopener noreferrer"><FaLinkedin  className="social-icon"/></Link>
            <Link to="https://x.com/HuebitsTech" target="_blank" rel="noopener noreferrer"><FaTwitter className="social-icon" /></Link>
            <Link to="https://www.instagram.com/huebits_tech/" target="_blank" rel="noopener noreferrer"><FaInstagram className="social-icon" /></Link>
            <Link to="https://www.youtube.com/@HuebitsTech" target="_blank" rel="noopener noreferrer"><FaYoutube className="social-icon" /></Link>
            <Link to="https://www.facebook.com/profile.php?id=61571753805069" target="_blank" rel="noopener noreferrer"><FaFacebook className="social-icon" /></Link>
        </div>
        </div>
        </footer>
       </div>
      </div>
      ) : (
          <div>
              <ShimmerSimpleGallery card imageHeight={180} row={3} col={3} gap={20} />
          </div>
      )}
    </div>
  );
}

export default Login;
